/**
 * The document list and the document view.
 *
 * This is the first screen that shows a real invoice, and the whole engine
 * hangs off it: database -> normalize -> preview -> print. Two rules govern
 * what may appear here.
 *
 * 1. The preview is the PRINT document, rendered by `renderPreview`. It is not
 *    a React re-implementation, because a preview that has drifted from the
 *    print is worse than no preview.
 * 2. The print buttons ask `port.capabilities` what this platform can do. The
 *    browser cannot save a PDF silently, and the UI says so rather than
 *    offering a button that fails.
 */
import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Printer, FileDown, AlertTriangle, Plus, Send } from 'lucide-react';
import { pagesFor, renderPreview, A4_HEIGHT_PX, A4_WIDTH_PX, fitScale } from '@core/doc/preview';
import type { NormalizedDocument } from '@core/doc/types';
import type { BusinessIdentity } from '@core/doc/renderA4';
import {
  printCapabilities,
  printDocument,
  saveDocumentPdf,
} from '@data/services/printService';
import { IssueTransitionError } from '@data/services/issueTransitionService';
// `documentActions` is a DATA-layer rule, not a view helper: it is the same
// function the write path must agree with, so it is imported from the reader
// rather than defined beside the screen that happens to use it today.
import { documentActions } from '@data/services/documentReader';
import {
  explainLoadFailure,
  summarise,
  useBusinessIdentity,
  useDocument,
  useIssueDocument,
  useRecentDocuments,
  useActiveBusiness,
  useStoredDocument,
} from './useDocuments';
import { toast } from '@ui/components/Toaster';
import { formatMoney } from '@core/money/format';
import { asMoney } from '@core/money/types';
import { InvoicePaymentHistory } from '@features/payments/InvoicePaymentHistory';
import { useCancelUnpaidInvoice } from '@features/payments/useInvoicePayments';
import { Button } from '@ui/components/Button';

/** Where the shipped fonts are, as a URL the app can actually fetch. */
const ASSETS_BASE_URL = new URL('assets/', document.baseURI).href;

/**
 * Turn a refused issue into words the user can act on.
 *
 * Its own function rather than reusing `explainLoadFailure`, because that one
 * knows about READ failures. Reusing it would mean a write failure reported with
 * a read-shaped message, or — worse — an unrecognised error falling through to
 * its generic branch and losing the specific reason the service refused.
 */
function explainIssueFailure(error: unknown): string {
  if (error instanceof IssueTransitionError) {
    switch (error.code) {
      case 'UNKNOWN_DOCUMENT':
        return 'That invoice no longer exists. It may have been deleted.';
      case 'NOT_ISSUABLE':
        // The lifecycle's own refusal, kept verbatim. "This document has already
        // been issued" is a better sentence than anything invented here, and
        // inventing a second phrasing is how two messages for one condition drift.
        return error.message;
      default:
        return error.message;
    }
  }
  if (error instanceof Error) return error.message;
  return 'The invoice could not be issued. Please try again.';
}

function PreviewFrame({
  html,
  scale,
  onPages,
}: {
  html: string;
  scale: number;
  onPages: (pages: number) => void;
}) {
  const ref = useRef<HTMLIFrameElement>(null);
  useEffect(() => {
    // srcdoc rather than src: the document is self-contained, and routing it
    // through a URL would let a stale cached copy of an old invoice appear.
    const frame = ref.current;
    if (frame) frame.srcdoc = html;
  }, [html]);

  /**
   * Measure the rendered page, so the user learns a 40-line invoice will print
   * on two sheets BEFORE the printer tells them.
   *
   * The measured height is divided back out of the scale: the frame displays a
   * scaled page, and the page inside it is what gets paginated. `pagesFor()`
   * then does the arithmetic purely, and is tested without a browser.
   */
  const measure = () => {
    const page = ref.current?.contentDocument?.querySelector('.doc-page');
    if (!page || scale <= 0) return;
    onPages(pagesFor(page.getBoundingClientRect().height / scale));
  };

  return (
    <iframe
      ref={ref}
      title="Document preview"
      data-testid="document-preview"
      onLoad={measure}
      // Sized to the scaled page so the frame matches it and no scrollbars
      // appear inside the document itself.
      style={{
        width: A4_WIDTH_PX * scale,
        height: A4_HEIGHT_PX * scale,
        border: 0,
      }}
      className="rounded bg-white shadow-lg"
    />
  );
}

/**
 * The document, its actions, and the preview.
 *
 * Extracted from the screen so the data has already been narrowed to a real
 * model by the time it arrives. The screen's `doc.data && biz.data &&` guard
 * used to be undone by four `!` assertions inside the handlers — which are
 * exactly the assertions that make a reviewer stop reading. Here the types
 * carry it, and `!` appears nowhere.
 */
function DocumentPane({
  documentId,
  status: storedStatus,
  outstandingPaise,
  model,
  business,
  canSavePdf,
  scale,
  busy,
  pages,
  onPages,
  onPrint,
  onSavePdf,
  canIssue: canIssueDoc,
  onIssue,
  canCancel,
  onCancel,
}: {
  documentId: string;
  status?: string;
  outstandingPaise?: number;
  model: NormalizedDocument;
  business: BusinessIdentity;
  canSavePdf: boolean;
  scale: number;
  busy: boolean;
  pages: number;
  onPages: (pages: number) => void;
  onPrint: () => void;
  onSavePdf: () => void;
  /** Only a DRAFT can be issued; anything else must not offer the button. */
  canIssue: boolean;
  onIssue: () => void;
  canCancel: boolean;
  onCancel: () => void;
}) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2" data-testid="document-actions">
        {/* Issuing is irreversible in the ways that matter: the document stops
            being deletable and becomes a tax document. So the button only exists
            for a DRAFT, rather than existing disabled with a tooltip. */}
        {canIssueDoc && (
          <button
            type="button"
            data-testid="issue-document"
            disabled={busy}
            onClick={onIssue}
            className="flex items-center gap-2 rounded bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          >
            <Send className="h-4 w-4" aria-hidden /> Issue invoice
          </button>
        )}

        {canCancel && (
          <Button variant="danger" size="sm" disabled={busy} onClick={onCancel}>Cancel invoice</Button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={onPrint}
          data-testid="document-print"
          className="flex items-center gap-2 rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
        >
          <Printer className="h-4 w-4" aria-hidden /> Print
        </button>

        {canSavePdf ? (
          <button
            type="button"
            disabled={busy}
            onClick={onSavePdf}
            className="flex items-center gap-2 rounded bg-slate-700 px-3 py-1.5 text-sm font-medium disabled:opacity-50"
          >
            <FileDown className="h-4 w-4" aria-hidden /> Save PDF
          </button>
        ) : (
          // The honest difference between the two targets, stated rather than
          // hidden behind a button that would fail.
          <span className="text-xs text-slate-500">
            To make a PDF here, choose Print and then “Save as PDF”. The desktop app
            saves directly.
          </span>
        )}
      </div>

      <PreviewFrame
        html={renderPreview(model, business, { scale }, ASSETS_BASE_URL)}
        scale={scale}
        onPages={onPages}
      />

      <p className="text-xs text-slate-500">
        {model.lines.length} line{model.lines.length === 1 ? '' : 's'} · will print on{' '}
        {pages} page{pages === 1 ? '' : 's'}
      </p>
      {documentId && storedStatus !== 'DRAFT' && storedStatus !== 'CANCELLED' && (
        <section className="w-full max-w-3xl rounded-lg border border-slate-800 bg-slate-900/50 p-4">
          <h2 className="mb-2 text-sm font-semibold">After this sale</h2>
          <p className="mb-3 text-xs text-slate-400">
            The invoice itself is never edited. If the customer returns goods, raise a sales
            return; if you need to reduce the bill without goods coming back, raise a credit
            note.
          </p>
          <div className="flex flex-wrap gap-2">
            <a
              data-testid="create-sales-return"
              href={`#/sales/returns/from-invoice/${documentId}`}
              className="rounded border border-slate-700 px-3 py-1.5 text-sm text-brand-200 hover:bg-slate-800"
            >
              Create sales return
            </a>
            <a
              data-testid="create-credit-note"
              href={`#/sales/credit-notes/from-invoice/${documentId}`}
              className="rounded border border-slate-700 px-3 py-1.5 text-sm text-brand-200 hover:bg-slate-800"
            >
              Create credit note
            </a>
          </div>
          <h2 className="mb-2 mt-4 text-sm font-semibold">Payment history</h2>
          <InvoicePaymentHistory documentId={documentId} />
          {outstandingPaise !== undefined && <p className="mt-2 text-xs text-slate-400">Outstanding: {moneyLabel(outstandingPaise)}</p>}
        </section>
      )}
    </>
  );
}

/**
 * The status badge on a list row.
 *
 * A DRAFT must not look like an ISSUED invoice. The two differ in the way that
 * matters most to a business - one is a number you can still change, the other
 * is a number a customer already holds - and a list that renders them
 * identically hides exactly the distinction the workflow turns on.
 *
 * Colour alone is not enough, so the WORD is present too: "Draft" vs "Issued" is
 * legible to a colour-blind user and in a greyscale print of the screen.
 */
const moneyLabel = (paise: number) => formatMoney(asMoney(paise));

function StatusPill({ status }: { status: string }) {
  const look =
    status === 'DRAFT'
      ? { label: 'Draft', className: 'bg-amber-500/15 text-amber-300' }
      : status === 'ISSUED'
        ? { label: 'Issued', className: 'bg-emerald-500/15 text-emerald-300' }
        : status === 'PARTIALLY_SETTLED'
          ? { label: 'Partially paid', className: 'bg-amber-500/15 text-amber-300' }
          : status === 'SETTLED'
            ? { label: 'Settled', className: 'bg-sky-500/15 text-sky-300' }
            : status === 'CANCELLED'
              ? { label: 'Cancelled', className: 'bg-slate-500/20 text-slate-400' }
              : { label: status, className: 'bg-slate-500/20 text-slate-400' };

  return (
    <span
      className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${look.className}`}
      data-testid="document-status"
    >
      {look.label}
    </span>
  );
}

export function DocumentScreen({ documentId }: { documentId: string | null }) {
  const [invoicePage, setInvoicePage] = useState(0);
  const activeBusiness = useActiveBusiness();
  const pageSize = 20;
  const list = useRecentDocuments(pageSize, 'SALES_INVOICE', invoicePage * pageSize);
  const biz = useBusinessIdentity();
  const doc = useDocument(documentId);
  const storedDoc = useStoredDocument(documentId);
  const issue = useIssueDocument();
  const cancel = useCancelUnpaidInvoice();
  const capabilities = printCapabilities();
  const [scale] = useState(() => {
    const isMobile = window.innerWidth < 768;
    const availableWidth = isMobile ? window.innerWidth * 0.9 : window.innerWidth * 0.55;
    return fitScale(availableWidth);
  });
  const [busy, setBusy] = useState(false);
  const [pages, setPages] = useState(1);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    try {
      await action();
    } catch (error) {
      toast({
        title: 'Could not print',
        description: explainLoadFailure(error),
        tone: 'danger',
      });
    } finally {
      flushSync(() => setBusy(false));
    }
  }

  /**
   * Issue the open document.
   *
   * Its own handler rather than going through `run`, because `run` reports every
   * failure as "Could not print" — which is actively wrong for a write: a
   * refused issue ("already been issued") is not a printer problem, and telling
   * a shopkeeper their printer failed when the document was fine would send them
   * looking in entirely the wrong place.
   */
  async function runCancel() {
    if (!documentId || !doc.data) return;
    const reason = window.prompt(`Why are you cancelling invoice ${doc.data.docNumber}?`);
    if (reason === null) return;
    if (!reason.trim()) {
      toast({ title: 'A cancellation reason is required', tone: 'danger' });
      return;
    }
    setBusy(true);
    try {
      await cancel.mutateAsync({ documentId, reason: reason.trim() });
      toast({ title: 'Invoice cancelled', description: 'The invoice can no longer receive a payment.', tone: 'success' });
    } catch (error) {
      toast({ title: 'Could not cancel the invoice', description: error instanceof Error ? error.message : 'Please try again.', tone: 'danger' });
    } finally {
      setBusy(false);
    }
  }

  async function runIssue() {
    if (!documentId) return;
    setBusy(true);
    try {
      const out = await issue.mutateAsync(documentId);
      toast({
        title: `Invoice ${out.docNumber} issued`,
        description: 'It is now a tax document and can no longer be deleted.',
        tone: 'success',
      });
    } catch (error) {
      toast({
        title: 'Could not issue the invoice',
        description: explainIssueFailure(error),
        tone: 'danger',
      });
    } finally {
      setBusy(false);
    }
  }

  if (documentId && (doc.isError || biz.isError)) {
    const error = doc.isError ? doc.error : biz.error;
    return (
      <div className="flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-amber-200">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
        <p className="text-sm">{explainLoadFailure(error)}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col p-4 text-slate-100">
      {/* Which company everything below belongs to. Always visible: the list, the
          letterhead, the GSTIN and the numbering series are all scoped to it. */}
      {/* List beside detail on a desktop; STACKED on a phone.
          `flex` alone is a ROW, and with a `w-72` sidebar that leaves 288 + 16 =
          304px of a 390px screen consumed before the detail pane begins. The
          detail pane is `flex-1` with `min-w-0`, so instead of overflowing it was
          squeezed to a ~22px sliver — technically inside the viewport, entirely
          unusable, with its own content clipped. On a phone the document list was
          visible and the document itself was not.

          The switch is at `lg`, not `xl`, on purpose: `AppShell` promotes the
          permanent nav sidebar at exactly `lg`, and a layout that went two-column
          only at `xl` would show a desktop nav bar beside a stacked document
          screen for the whole 1024-1280px band. One breakpoint, used consistently,
          is what keeps the two halves agreeing about what "desktop" means.

          `h-full` is desktop-only for the same reason: pinned height is what makes
          the two columns share a viewport. Stacked, it would fight the page's own
          scrolling. */}
      <div className="flex flex-col gap-4 lg:h-full lg:flex-row">
      <aside className="w-full shrink-0 overflow-y-auto rounded-lg border border-slate-800 bg-slate-900/60 lg:w-72">
        <h2 className="border-b border-slate-800 px-3 py-2 text-xs uppercase tracking-wide text-slate-400">
          Invoices
        </h2>
        {/* The way in to the editor. A list screen that can only show documents
            already saved is read-only by accident, and an invoice nobody can
            raise is not a working till. */}
        <a
          href="#/documents/new"
          data-testid="new-invoice"
          className="flex items-center gap-2 border-b border-slate-800 px-3 py-2 text-sm font-medium text-brand-400 hover:bg-slate-800/60"
        >
          <Plus className="h-4 w-4" aria-hidden />
          New sales invoice
        </a>
        {list.data && list.data.total > pageSize && (
          <nav
            aria-label="Invoice list pagination"
            className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-slate-800 bg-slate-900 px-3 py-2"
          >
            <Button
              size="sm"
              variant="ghost"
              aria-label="Previous invoice page"
              disabled={invoicePage === 0 || list.isFetching}
              onClick={() => flushSync(() => setInvoicePage((page) => Math.max(0, page - 1)))}
            >
              Previous
            </Button>
            <span className="text-xs text-slate-400" aria-live="polite">
              Page {invoicePage + 1} of {Math.ceil(list.data.total / pageSize)}
            </span>
            <Button
              size="sm"
              variant="ghost"
              aria-label="Next invoice page"
              disabled={(invoicePage + 1) * pageSize >= list.data.total || list.isFetching}
              onClick={() => flushSync(() => setInvoicePage((page) => page + 1))}
            >
              Next
            </Button>
          </nav>
        )}
        {list.isLoading && <p className="p-3 text-sm text-slate-500">Loading…</p>}
        {(list.data?.documents.length ?? 0) === 0 && (
          <p className="p-3 text-sm text-slate-500">
            No invoices yet. Imported invoices appear here.
          </p>
        )}
        {/*
          The row.

          Built from `documentActions(status)` rather than from `status` inline,
          so what the list offers and what the write path permits are the same
          function. The DRAFT row is the important one: it is the only row with
          an Edit control, and it is how a user returns to work they left. An
          ISSUED row deliberately has no Edit - the invoice is a record, and the
          service would refuse the save anyway.
        */}
        <ul>
          {list.data?.documents.map((d) => {
            const actions = documentActions(d.status);
            // Each row reads its OWN frozen snapshot name, by document id. Keyed
          // by party id it was one shared entry per customer, so every invoice
          // for a renamed customer rendered the newest name and quietly
          // disagreed with the older invoices it links to.
          const customer = list.data?.partyNamesByDocumentId.get(d.id);
            return (
              <li key={d.id} className="border-b border-slate-800/60 last:border-b-0">
                <div
                  className={`px-3 py-2 ${
                    d.id === documentId ? 'bg-slate-800' : 'hover:bg-slate-800/60'
                  }`}
                >
                  <a
                    href={`#/documents/${d.id}`}
                    className="block text-left"
                    data-testid="document-row"
                    data-status={d.status}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium text-slate-200">{d.docNumber}</span>
                      <StatusPill status={d.status} />
                    </div>
                    {/*
                      `summarise` carries the document TYPE, and the type is the
                      one thing this row cannot be rebuilt around later.

                      The list mixes quotations, orders and invoices, and the
                      number alone does not say which is which — so a mixed list
                      renders 46 quotations as if they were invoices, and the
                      user's conclusion is that their import was wrong. It was
                      not; the LIST could not tell them apart.
                    */}
                    <div className="block text-xs text-slate-400">{summarise(d)}</div>
                    <div className="block text-xs text-slate-400">{customer ?? '—'}</div>
                    <div className="mt-0.5 flex items-baseline justify-between gap-2 text-xs">
                      <span className="text-slate-500">{d.docDate}</span>
                      {d.docType === 'SALES_INVOICE' && <span className="text-slate-400">Outstanding {moneyLabel(d.balancePaise)}</span>}
                    </div>
                  </a>
                  {/*
                    Actions live OUTSIDE the link on purpose. Nesting a button
                    inside an anchor is invalid HTML, and the click would also
                    navigate, so "Edit" would land on the document screen instead
                    of the editor - which is the bug this whole workflow exists
                    to fix.
                  */}
                  {(actions.canEdit || actions.canIssue) && (
                    <div className="mt-1.5 flex gap-2 pl-0.5">
                      {actions.canEdit && (
                        <a
                          href={`#/invoices/${d.id}/edit`}
                          data-testid="document-edit"
                          className="rounded border border-slate-700 px-2 py-0.5 text-xs text-brand-400 hover:border-brand-500"
                        >
                          Edit draft
                        </a>
                      )}
                      {actions.canIssue && (
                        <a
                          href={`#/documents/${d.id}`}
                          data-testid="document-issue"
                          className="rounded border border-slate-700 px-2 py-0.5 text-xs text-slate-300 hover:border-slate-500"
                        >
                          Preview &amp; issue
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        {/* State the visible range so paging never looks like missing data. */}
        {list.data && list.data.total > 0 && (
          <p className="border-t border-slate-800 px-3 py-2 text-xs text-slate-500">
            Showing {invoicePage * pageSize + 1}–
            {Math.min((invoicePage + 1) * pageSize, list.data.total)} of {list.data.total} invoices.
          </p>
        )}
      </aside>

      <section className="flex min-w-0 flex-1 flex-col items-center gap-3 overflow-auto">
        {!documentId && <p className="text-sm text-slate-500">Choose a document to preview it.</p>}
        {documentId && (activeBusiness.isLoading || doc.isLoading || biz.isLoading) && (
          <p className="text-sm text-slate-500">Loading…</p>
        )}

        {doc.data && biz.data && (
          <DocumentPane
            documentId={documentId as string}
            status={storedDoc.data?.status}
            outstandingPaise={storedDoc.data?.balancePaise}
            model={doc.data}
            business={biz.data}
            canSavePdf={capabilities.silentPdf}
            scale={scale}
            busy={busy}
            pages={pages}
            onPages={setPages}
            canIssue={doc.data.status === 'DRAFT'}
            onIssue={() => void runIssue()}
            canCancel={storedDoc.data?.docType === 'SALES_INVOICE' && storedDoc.data.status === 'ISSUED' && storedDoc.data.allocatedPaise === 0}
            onCancel={() => void runCancel()}
            onPrint={() =>
              run(async () => {
                await printDocument(doc.data, biz.data, ASSETS_BASE_URL);
              })
            }
            onSavePdf={() =>
              run(async () => {
                const r = await saveDocumentPdf(doc.data, biz.data, ASSETS_BASE_URL);
                if (r.saved) {
                  toast({ title: 'PDF saved', description: r.filePath, tone: 'success' });
                }
              })
            }
          />
        )}
      </section>
      </div>
    </div>
  );
}
